/**
 * Procedural bike + rider. No external assets — every part is placed from the same
 * geometry numbers the sim uses, so the thing you see is the thing being simulated.
 *
 * The rules this file keeps, in order of how much they cost when broken:
 *
 * 1. **Every dimension comes out of `Tuning.bike`.** Wheelbase, wheel radius, head
 *    angle and fork offset are the sim's; the handful of numbers the sim has no opinion
 *    about (chainstay, bottom bracket drop, axle-to-crown, bar width) live here as
 *    local constants and everything else is derived. Change the head angle in tuning
 *    and the fork visibly slackens, because the steering axis is *built* from it.
 *
 * 2. **The root sits on the contact line, not on the axle line.** `root` is placed at
 *    BikeState.y minus comHeight, which is the ground under the bike, so the axles have
 *    to be drawn a wheel radius above it. The previous version drew them *on* it, which
 *    buried both wheels to their hubs.
 *
 * 3. **The wheels are in the XY plane.** They spin about Z, so a torus for the casing
 *    belongs in XY. The previous version rotated them a quarter turn onto the ZY plane,
 *    which mounted them across the bike like a shopping trolley's — most of why the
 *    thing read as a unicycle from behind.
 *
 * 4. **Suspension is drawn as a correction from sag, not from zero.** The sim's
 *    `travel` already sits at sagFraction when the bike is at rest, so the drawn wheel
 *    moves by `travel - sag`. Drawing raw travel parks both wheels 50 mm up inside the
 *    fork and swingarm while the bike is standing still.
 *
 * 5. **Rigid groups are merged into one buffer, with colour in the vertices.** This rule
 *    was written in wave 1 and then not kept: wave 2 shipped 26 primitive meshes and the
 *    integrator's draw-call audit measured bike + rider at **46 calls**, the single
 *    biggest line in the game, because every one of them is drawn once for beauty and
 *    again for the shadow map.
 *
 *    It is kept now, and completely. Every rigid group — chassis, swingarm, steerer,
 *    fork lower, both wheels, cranks, both pedals, and all ten of the rider's bones — is
 *    a `THREE.Bone`, and the entire kit is **one `THREE.SkinnedMesh`**: 2 draw calls
 *    (beauty + shadow) plus the two contact-shadow blobs, which are transparent and
 *    cannot join. 46 -> 4. Nothing about the authoring changed: `Skin` bakes each bone's
 *    bind matrix into its own vertices as it merges, so a part is still built in its own
 *    group's local coordinates.
 *
 *    The three materials that used to force separate meshes are one material now, with
 *    roughness in a vertex attribute. See `createKitMaterial` in bike/rider.ts.
 *
 *    **Measured, wave 3 round 3, rather than claimed.** `captures/w3-rider-view.mjs`
 *    renders the committed frame twice with the kit's root shown and hidden, with the
 *    shadow map off so the shadow camera's fit cannot move between the two:
 *
 *        beautyCalls 3   meshes [bikeRider(castShadow), Mesh, Mesh]   shadowMaps 1
 *        triangles   19,640
 *
 *    3 beauty (the one skinned mesh plus the two transparent contact blobs) + 1 shadow
 *    (only the skinned mesh casts, and there is one shadow-casting light) = **4**, against
 *    the wave-2 audit's 46. Everything added since — every loft on the trousers, the base
 *    layer, the pack and the saddle — cost triangles and not one draw call, which is the
 *    entire point of the rule.
 *
 *    Two traps if you re-measure it: `post.ts` owns the frame and turns
 *    `renderer.info.autoReset` off, so `info.render.calls` is cumulative unless you reset
 *    it by hand; and with shadows on, hiding the bike re-fits the shadow camera and pulls
 *    a different set of terrain rings into the depth pass. The first run of the probe hit
 *    both and reported *minus 67* draw calls for this piece.
 *
 * 6. **Metalness stays low.** There is no environment map in this build, so a
 *    metalness-0.9 rim has no indirect specular to reflect and renders black. Bright
 *    albedo with a tight roughness reads as metal here; PBR-correct metal does not.
 */

import * as THREE from 'three';
import { Tuning } from '../core/tuning';
import type { BikeState } from '../bike/physics';
import { clamp, clamp01, damp } from '../core/math';
import {
  PartBuilder, RiderRig, Skin, createKitMaterial, riderInput, type Cockpit,
} from '../bike/rider';
import { declareInvariant } from '../core/audit';

/**
 * The other half of the wave-5 coherence gap: **"the bike is completely gone
 * (submerged)"** on a crash.
 *
 * It is not a drawing bug. While `s.crashed` is held the balance assist is off and the
 * suspension cannot lift a chassis that is already below where its own wheels are, so the
 * sim's own state has the bike inside the hillside — and this file drew exactly that,
 * faithfully, for the 2.4 s the flag lasts. The delivered frame is a rider's torso lying
 * on bare dirt with no bike in it at all, six times a run.
 *
 * This measures the drawn wheel against the ground the sim sampled under it. It is
 * observed ONLY while crashed: the ordinary-riding case is ride-feel's P0 gap this wave
 * (a wheel 200 mm under for 6% of `fullrun` frames) and folding that into this number
 * would both hide their defect and blow this budget for a reason that is not this
 * piece's to fix.
 */
const CRASH_SINK = declareInvariant({
  id: 'rider.crashBikeSinkM',
  owner: 'rider',
  what: 'drawn wheel contact below the sampled ground while the sim holds crashed',
  unit: 'm',
  budget: 0.06,
  consequence: 'the bike disappears into the hillside and the crash reads as the rider lying on bare dirt',
  source: 'src/render/bikeModel.ts CRASH_LIFT',
});

/** How much of the measured burial the drawn bike is allowed to climb out of, per second
 *  of blend. See the crash block in `update`. */
const CRASH_LIFT = { inRate: 13, outRate: 3.4, max: 1.6 } as const;

// --- geometry the sim does not have an opinion about -------------------------------
/** Rear axle to bottom bracket, m. Front centre falls out of Tuning.bike.wheelbase. */
const CHAINSTAY = 0.437;
/** Bottom bracket below the axle line, m. */
const BB_DROP = 0.028;
/** Fork axle-to-crown, m — a 170 mm 29er. */
const AXLE_TO_CROWN = 0.565;
const HEAD_TUBE = 0.125;
/** Bar width, m. A modern enduro cockpit is 780 mm and has to look it. */
const BAR_WIDTH = 0.780;
const CRANK_LEN = 0.170;
/** Half the Q-factor, m — how far out the pedals sit. */
const PEDAL_Z = 0.078;
/** Seat tube angle from horizontal, rad. */
const SEAT_ANGLE = 77 * (Math.PI / 180);
/**
 * Saddle height along the seat tube from the BB, m. A slammed dropper, not an XC post.
 *
 * This number is load-bearing for the rider, not just for the bike's silhouette: the
 * rig refuses to put the hips through the saddle, so the saddle top is the floor on how
 * low the rider can get before they have to move behind it. At 0.50 the saddle nose sat
 * 0.87 m off the ground and that floor ate the entire tuck. 0.435 puts the rails at
 * 0.77 m, which is where a 170 mm dropper actually parks them.
 */
const SADDLE_UP = 0.435;
/** Swingarm pivot, chassis space. */
const PIVOT_X = 0.028;
const PIVOT_Y = 0.402;
/** Crank angle the outside foot drops to per radian of lean while coasting, rad. */
const OUTSIDE_FOOT = 0.5;
/** Crank turns per wheel turn — a 32:11 top gear. */
const GEAR = 0.34;
/** Peak darkening of the contact shadow. */
const SHADOW_ALPHA = 0.72;

/**
 * **The colourway is decided, in docs/BAR.md, and this is it — not a reinterpretation.**
 *
 * > Raw silver frame. Orange Fox 38 fork.
 *
 * | part | treatment |
 * |---|---|
 * | frame | raw brushed silver — high value, near-neutral, faintly warm |
 * | fork lowers | saturated Fox orange, the single loudest thing on the bike |
 * | stanchions | gold/bronze Kashima |
 * | drivetrain, rotors, chainring | raw silver, slightly darker and cooler than the frame |
 * | bars, stem, cranks, saddle, post, rims | black |
 * | tyres | near-black, with a small warm note on the sidewall |
 *
 * Why it is right, so nobody improves it back: the world is brown dirt, green timber and
 * warm light, all mid-value. Silver separates from that by **value**, which survives
 * shadow and every time of day; hue separation does not. The previous build had the frame
 * at a cool anodised 137 — inside the band the terrain itself occupies — and three waves
 * of critics said the bike does not read against the ground.
 *
 * The ladder against the rider it shares a mesh with, **updated in wave 4** when the
 * rider's kit was rebuilt to the reference's two-value scheme (see `K` in bike/rider.ts):
 * jersey 199, frame 189, tyre 38, rim 47, trouser 85, helmet 106. The frame is no longer
 * the brightest thing on screen — the rider's jersey is, by 10 luma — and that is the
 * right order, because from the chase camera the rider occludes almost the whole bike.
 *
 * What the frame's value is now FOR, and it is a bigger job than it was: **the seat tube
 * and shock are the bright column the eye sees between the rider's two legs.** Wave 4
 * opened a real gap there, and a silver frame in it is what turns that gap from a dark
 * slot between two dark legs into a hard light/dark/light edge pair. Measured dead astern:
 * the legs render 2–13 luma and the column between them 179–194. If anyone ever darkens
 * the frame, that read goes with it.
 *
 * **The metalness trap, which BAR names and which this file's header has always named:**
 * there is no environment map in this build, so `metalness: 0.9` has no indirect specular
 * to reflect and renders BLACK. Every "metal" here is bright albedo at a tight roughness
 * on a 0.08-metalness material. Verified in a capture, not assumed.
 */
const BK = {
  frame: 0xc0bdb5,
  /** Frame hardware that catches the key — rocker plates, link axles. */
  frameHi: 0xd5d2c9,
  /** The black parts that are structurally frame: stem, shock body, dropper, caliper. */
  frameDark: 0x2c2f34,
  /** Bars and cranks. BAR lists both as black and they are the rider's own contact
   *  points, so they want to disappear rather than compete with the fork. */
  bar: 0x26292e,
  /** Bright machined steel: shock shaft, BB spindle, brake levers. */
  shaft: 0xcdd0d1,
  accent: 0xc9683a,
  /** Fox orange. Brighter and more saturated than the identity accent on purpose — it is
   *  a painted physical part catching direct sun, not a UI colour. */
  lower: 0xe8621f,
  stanchion: 0xd0a95a,
  // Rims are black now, per BAR. They were 176 luma, which made a spinning wheel the
  // brightest object in tree shade; with a silver frame above them there is no longer any
  // reason for the wheel to carry value at all — it is the one part of a bike that really
  // is black on almost every real bike.
  rim: 0x303338,
  spoke: 0x8b9097,
  hub: 0x9ba1a7,
  rotor: 0xa6abaf,
  /** Raw silver, not the bronze it was: BAR puts the whole drivetrain in silver. */
  ring: 0xa3a8ab,
  tire: 0x24272b,
  /** The warm note on the sidewall. Small, and the only warm thing below the fork. */
  sidewall: 0x4a3a2a,
  lug: 0x191c20,
  grip: 0x33383f,
  saddle: 0x1e2126,
  /** The saddle's perimeter welt. An edge on a near-black object has to be lighter than
   *  the object or there is no edge — the form alone cannot carry it in shadow. */
  saddleEdge: 0x3d434b,
  pedal: 0x34383e,
  sole: 0x1c1f23,
  shoe: 0x454c55,
} as const;

/** Roughness / micro-relief pairs. Roughness lives in a vertex attribute now. */
const S_FRAME: [number, number] = [0.46, 0.0];
const S_METAL: [number, number] = [0.26, 0.0];
const S_RUBBER: [number, number] = [0.88, 0.55];
const S_LEATHER: [number, number] = [0.74, 0.35];

const _v = new THREE.Vector3();
const _inv = new THREE.Matrix4();
const UPV = new THREE.Vector3(0, 1, 0);
const TWO_PI = Math.PI * 2;

function wrap2Pi(a: number): number {
  if (a >= -TWO_PI && a <= TWO_PI) return a;
  return a - Math.floor(a / TWO_PI) * TWO_PI;
}

/**
 * Soft radial blob for the contact shadow. Built once, no DOM, no asset file.
 *
 * Two lobes, not one. A single smooth falloff spreads the darkening evenly over the
 * whole plane and reads as a grey smudge the bike happens to be standing near; what
 * grounds an object is a *tight, dark core* right at the contact patch with a wide,
 * faint halo around it, because that is the shape real contact occlusion has.
 */
function shadowTexture(): THREE.DataTexture {
  const N = 64;
  const data = new Uint8Array(N * N * 4);
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const u = ((x + 0.5) / N) * 2 - 1;
      const v = ((y + 0.5) / N) * 2 - 1;
      const r = Math.min(1, Math.hypot(u, v));
      const s = 1 - r;
      const i = (y * N + x) * 4;
      data[i] = 255; data[i + 1] = 255; data[i + 2] = 255;
      data[i + 3] = Math.round(
        Math.min(1, 0.62 * Math.pow(s, 2.6) + 0.44 * Math.pow(s, 1.2)) * 255,
      );
    }
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  t.minFilter = THREE.LinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

export class BikeModel {
  readonly root = new THREE.Group();

  // Every one of these used to be a Group with its own Mesh. They are bones now: the
  // hierarchy, the local transforms and the update code below are identical, but the
  // geometry they carry lives in one shared buffer.
  private readonly chassis = new THREE.Bone();
  private readonly rearArm = new THREE.Bone();
  private readonly steer = new THREE.Bone();
  private readonly forkLower = new THREE.Bone();
  private readonly frontWheel = new THREE.Bone();
  private readonly rearWheel = new THREE.Bone();
  private readonly cranks = new THREE.Bone();
  private readonly pedal: THREE.Bone[] = [];
  private readonly gripAnchor: THREE.Object3D[] = [];
  private readonly pedalAnchor: THREE.Object3D[] = [];

  private readonly rig: RiderRig;
  private readonly cockpit: Cockpit = {
    gripL: new THREE.Vector3(), gripR: new THREE.Vector3(),
    pedalL: new THREE.Vector3(), pedalR: new THREE.Vector3(),
    bbY: 0, saddleX: 0, saddleTop: 0,
  };

  private readonly shadowMesh: THREE.Mesh[] = [];
  private readonly shadowMat: THREE.MeshBasicMaterial[] = [];

  /** cos(head angle): the drawn fork travels along its own axis, not vertically. */
  private readonly cosH: number;
  private readonly swingLen: number;
  private crank = 0;
  private lastT = -1;
  /** 0 riding, 1 down. Matches the rider rig's own blend so the two agree frame for frame. */
  private crashMix = 0;

  constructor() {
    const B = Tuning.bike;
    const WR = B.wheelRadius;
    const HA = B.headAngle;
    const sinH = Math.sin(HA);
    const cosH = Math.cos(HA);
    this.cosH = cosH;

    const skin = new Skin();

    // --- key points, chassis space (y = 0 is the contact line, x = 0 is the BB) -----
    const rearX = -CHAINSTAY;
    const frontX = B.wheelbase - CHAINSTAY;
    const bbY = WR - BB_DROP;
    // The steering axis, resolved from head angle and fork offset exactly as the sim's
    // trail calculation does, so the drawn front centre and the simulated one agree.
    const axisX = frontX - B.forkOffset * cosH;
    const axisY = WR - B.forkOffset * sinH;
    const crownX = axisX - AXLE_TO_CROWN * sinH;
    const crownY = axisY + AXLE_TO_CROWN * cosH;
    /** Steerer-local (u = perpendicular-forward, v = along the axis) into chassis space. */
    const stx = (u: number, v: number) => crownX + u * cosH - v * sinH;
    const sty = (u: number, v: number) => crownY + u * sinH + v * cosH;

    const headLoX = stx(0, 0.006), headLoY = sty(0, 0.006);
    const headHiX = stx(0, HEAD_TUBE), headHiY = sty(0, HEAD_TUBE);
    const stDirX = -Math.cos(SEAT_ANGLE), stDirY = Math.sin(SEAT_ANGLE);
    const seatTopX = stDirX * 0.333, seatTopY = bbY + stDirY * 0.333;
    const saddleX = stDirX * SADDLE_UP, saddleY = bbY + stDirY * SADDLE_UP;

    this.cockpit.bbY = bbY;
    this.cockpit.saddleX = saddleX + 0.02;
    this.cockpit.saddleTop = saddleY + 0.036;

    // --- the bone hierarchy, at its bind (rest) transforms --------------------------
    const raX = rearX - PIVOT_X;
    const raY = WR - PIVOT_Y;
    this.swingLen = Math.hypot(raX, raY);

    this.chassis.name = 'chassis';
    this.rearArm.name = 'rearArm';
    this.steer.name = 'steer';
    this.forkLower.name = 'forkLower';
    this.frontWheel.name = 'frontAxle';
    this.rearWheel.name = 'rearAxle';
    this.cranks.name = 'cranks';

    this.rearArm.position.set(PIVOT_X, PIVOT_Y, 0);
    this.rearWheel.position.set(raX, raY, 0);
    this.rearArm.add(this.rearWheel);
    this.chassis.add(this.rearArm);

    this.steer.position.set(crownX, crownY, 0);
    // 'ZYX' composes as Rz * Ry, i.e. the bars turn about the *tilted* head tube axis
    // rather than about vertical. With the order the other way round a slack fork
    // steers like a shopping trolley castor.
    this.steer.rotation.order = 'ZYX';
    this.steer.rotation.z = HA;
    this.frontWheel.position.set(B.forkOffset, -AXLE_TO_CROWN, 0);
    this.forkLower.add(this.frontWheel);
    this.steer.add(this.forkLower);
    this.chassis.add(this.steer);

    this.cranks.position.set(0, bbY, 0);
    this.chassis.add(this.cranks);
    for (const sz of [1, -1]) {
      const g = new THREE.Bone();
      g.name = sz > 0 ? 'pedalBoneR' : 'pedalBoneL';
      g.position.set(sz * CRANK_LEN, 0, sz * PEDAL_Z);
      this.cranks.add(g);
      this.pedal.push(g);
    }
    this.root.add(this.chassis);

    const iChassis = skin.bone(this.chassis);
    const iRearArm = skin.bone(this.rearArm);
    const iRearWheel = skin.bone(this.rearWheel);
    const iSteer = skin.bone(this.steer);
    const iForkLower = skin.bone(this.forkLower);
    const iFrontWheel = skin.bone(this.frontWheel);
    const iCranks = skin.bone(this.cranks);
    const iPedal = [skin.bone(this.pedal[0]), skin.bone(this.pedal[1])];

    // --- front triangle ------------------------------------------------------------
    {
      const f = new PartBuilder();
      // The frame's underside goes dark. The bike is lit by one key and a 0.10 ambient,
      // so without this the down tube and the chainstays are the same tone top and
      // bottom and the frame reads as a flat decal laid over the wheels.
      f.underShade(0.36);
      f.surf(S_FRAME[0], S_FRAME[1]);
      // Head tube, on the steering axis but fixed to the frame.
      f.rod(headLoX, headLoY, 0, headHiX, headHiY, 0, 0.041, BK.frame, 0.038, 10);
      // Down tube: BB to the bottom of the head tube. Fat, ovalised, the visual spine
      // of a modern enduro frame — and the one place a colour accent belongs.
      const dtAx = 0.030, dtAy = bbY + 0.030;
      const dtBx = stx(0, 0.030), dtBy = sty(0, 0.030);
      const dtAng = Math.atan2(dtBy - dtAy, dtBx - dtAx);
      f.rod(dtAx, dtAy, 0, dtBx, dtBy, 0, 0.046, BK.frame, 0.040, 10);
      f.box(0.060, 0.190, 0.116,
        dtAx + (dtBx - dtAx) * 0.34, dtAy + (dtBy - dtAy) * 0.34, 0,
        BK.frame, dtAng - Math.PI / 2);
      f.box(0.052, 0.080, 0.120,
        dtAx + (dtBx - dtAx) * 0.60, dtAy + (dtBy - dtAy) * 0.60, 0,
        BK.accent, dtAng - Math.PI / 2);
      // Top tube down to the seat cluster.
      f.rod(seatTopX - 0.004, seatTopY + 0.028, 0, stx(0, 0.098), sty(0, 0.098), 0,
        0.030, BK.frame, 0.027, 8);
      // Seat tube plus a slammed dropper. It has to be low: a bike drawn with the
      // saddle up is a cross-country bike whatever its head angle says.
      f.rod(0.010, bbY + 0.012, 0, seatTopX, seatTopY, 0, 0.032, BK.frame, 0.029, 8);
      f.seg(seatTopX, seatTopY - 0.02, 0, saddleX, saddleY - 0.015, 0,
        0.0165, BK.frameDark, 0.0165, 8);
      f.axle(0, bbY, 0, 0.045, 0.090, BK.frameDark, 12);
      // Shock in the front triangle, and the rocker it drives.
      f.seg(0.272, bbY + 0.302, 0, 0.078, bbY + 0.232, 0, 0.029, BK.frameDark, 0.029, 8);
      f.box(0.075, 0.062, 0.052, 0.288, bbY + 0.322, 0, BK.frameDark, -0.34);
      f.surf(S_METAL[0], S_METAL[1]);
      f.seg(0.078, bbY + 0.232, 0, 0.012, bbY + 0.208, 0, 0.0125, BK.shaft, 0.0125, 8);
      f.surf(S_FRAME[0], S_FRAME[1]);
      for (const sz of [1, -1]) {
        f.box(0.115, 0.048, 0.028, -0.018, bbY + 0.248, sz * 0.032, BK.frameHi, 0.42);
      }
      // Junctions. Every place two tubes meet gets a crease, which is what tells the eye
      // this is a welded structure rather than a set of unrelated cylinders — and the
      // one under the saddle is the crease that keeps the top tube out of the rider's
      // shorts from the chase camera.
      f.shade(dtAx + 0.010, dtAy - 0.010, 0, 0.150, 0.45);        // BB / down tube
      f.shade(dtBx - 0.020, dtBy - 0.030, 0, 0.150, 0.42);        // head tube / down tube
      f.shade(seatTopX, seatTopY - 0.010, 0, 0.150, 0.45);        // seat cluster
      f.shade(saddleX + 0.010, saddleY - 0.070, 0, 0.190, 0.52);  // under the saddle
      f.shade(0.020, bbY + 0.150, 0, 0.170, 0.38);                // inside the front triangle
      skin.part(f.build(), iChassis);

      // --- saddle ---------------------------------------------------------------------
      // It was two ellipsoids, which from every angle read as a black lozenge lying on the
      // seat tube. A saddle has the most recognisable plan-form on a bike — a narrow nose,
      // a waist, a flared tail — and none of that survives two blobs. This is a swept
      // section instead: seven stations, each with its own half-width, thickness and
      // height, so the outline tapers and the profile dips and kicks up at the back the
      // way a real shell does. The perimeter welt is a value step up on a near-black
      // object, which is the only way an edge reads when the whole part is in shadow, and
      // the rails are bright metal because the chase camera looks at the *underside* of
      // this part more often than at the top of it.
      const sd = new PartBuilder();
      sd.underShade(0.42);
      sd.surf(S_LEATHER[0], S_LEATHER[1]);
      /** station: [x from the saddle datum, half-width, height offset, half-thickness] */
      const shell: [number, number, number, number][] = [
        [0.126, 0.013, 0.010, 0.010],
        [0.092, 0.026, 0.003, 0.013],
        [0.056, 0.039, -0.002, 0.016],
        [0.018, 0.050, -0.004, 0.018],
        [-0.022, 0.062, -0.001, 0.019],
        [-0.062, 0.072, 0.006, 0.020],
        [-0.098, 0.066, 0.014, 0.017],
        [-0.122, 0.044, 0.018, 0.013],
      ];
      for (const [dx, hw, dy, ht] of shell) {
        sd.blob(saddleX + dx, saddleY + dy, 0, 0.026, ht, hw, BK.saddle, 10);
      }
      for (let k = 0; k < shell.length - 1; k++) {
        for (const sz of [1, -1]) {
          sd.seg(saddleX + shell[k][0], saddleY + shell[k][2] + shell[k][3] * 0.45,
            sz * shell[k][1] * 0.94,
            saddleX + shell[k + 1][0], saddleY + shell[k + 1][2] + shell[k + 1][3] * 0.45,
            sz * shell[k + 1][1] * 0.94,
            0.0055, BK.saddleEdge, 0.0055, 5);
        }
      }
      sd.surf(S_METAL[0], S_METAL[1]);
      for (const sz of [1, -1]) {
        sd.seg(saddleX + 0.062, saddleY - 0.024, sz * 0.020,
          saddleX - 0.072, saddleY - 0.014, sz * 0.024, 0.0055, BK.shaft, 0.0055, 6);
      }
      sd.box(0.052, 0.030, 0.062, saddleX - 0.014, saddleY - 0.034, 0, BK.frameDark, 0.06);
      sd.shade(saddleX - 0.010, saddleY - 0.026, 0, 0.090, 0.46);   // under the shell
      skin.part(sd.build(), iChassis);
    }

    // --- swingarm ------------------------------------------------------------------
    {
      const a = new PartBuilder();
      a.underShade(0.34);
      a.surf(S_FRAME[0], S_FRAME[1]);
      for (const sz of [1, -1]) {
        a.rod(-0.030, -0.040, sz * 0.062, raX + 0.030, raY, sz * 0.040, 0.026, BK.frame, 0.019, 7);
        a.rod(raX + 0.026, raY + 0.010, sz * 0.038, -0.098, 0.163, sz * 0.048, 0.019, BK.frame, 0.017, 7);
        a.box(0.072, 0.080, 0.020, raX, raY, sz * 0.040, BK.frameDark, -0.1);
      }
      a.axle(0, 0, 0, 0.036, 0.105, BK.frameDark, 12);
      a.axle(-0.098, 0.163, 0, 0.024, 0.110, BK.frameHi, 10);
      a.box(0.090, 0.050, 0.135, -0.075, -0.020, 0, BK.frame);
      skin.part(a.build(), iRearArm);
    }

    // --- wheels --------------------------------------------------------------------
    const buildWheel = (bone: number, driven: boolean) => {
      const rimR = WR - 0.062;
      const t = new PartBuilder();
      t.surf(S_RUBBER[0], S_RUBBER[1]);
      // Casing, squashed on Z to a real 2.5" width rather than a fat doughnut.
      t.ring(0, 0, 0, WR - 0.031, 0.031, 0.062, BK.tire, 6, 30);
      // Knobs. Cheap, merged, and the strongest "this is a mountain bike" cue the
      // silhouette has at any distance where the frame is already mush.
      for (let i = 0; i < 20; i++) {
        const ang = (i / 20) * Math.PI * 2;
        const r = WR - 0.008;
        t.box(0.024, 0.034, 0.050, Math.cos(ang) * r, Math.sin(ang) * r, 0, BK.lug, ang);
        if (i % 2 === 0) {
          const r2 = WR - 0.028;
          for (const sz of [1, -1]) {
            t.box(0.020, 0.030, 0.020, Math.cos(ang) * r2, Math.sin(ang) * r2, sz * 0.030, BK.lug, ang);
          }
        }
      }
      // The warm note on the sidewall BAR asks for: a band just above the rim on each
      // side. It is the only warm thing below the fork, and on a black rim and a black
      // tyre it is the one cue that tells the eye the casing has a *side* at all.
      for (const sz of [1, -1]) {
        t.ring(0, 0, sz * 0.026, WR - 0.054, 0.009, 0.008, BK.sidewall, 3, 22);
      }
      t.surf(S_METAL[0], S_METAL[1]);
      t.ring(0, 0, 0, rimR, 0.017, 0.030, BK.rim, 5, 26);
      t.ring(0, 0, 0, rimR - 0.014, 0.007, 0.028, BK.rim, 4, 24);
      t.axle(0, 0, 0, 0.031, 0.108, BK.hub, 12);
      t.axle(0, 0, 0.052, 0.040, 0.026, BK.hub, 10);
      t.axle(0, 0, -0.052, driven ? 0.052 : 0.040, 0.030, BK.hub, 10);
      for (let i = 0; i < 12; i++) {
        const ang = (i / 12) * Math.PI * 2;
        const sz = i % 2 === 0 ? 0.042 : -0.042;
        t.seg(0, 0, sz, Math.cos(ang) * rimR, Math.sin(ang) * rimR, 0, 0.0035, BK.spoke, 0.0035, 4);
      }
      // Rotor, left side, where a real one is.
      t.ring(0, 0, -0.058, 0.098, 0.0035, 0.005, BK.rotor, 3, 22);
      t.ring(0, 0, -0.058, 0.070, 0.0035, 0.005, BK.rotor, 3, 18);
      skin.part(t.build(), bone);
    };
    buildWheel(iFrontWheel, false);
    buildWheel(iRearWheel, true);

    // --- fork + cockpit ------------------------------------------------------------
    {
      const c = new PartBuilder();
      c.underShade(0.34);
      c.surf(S_FRAME[0], S_FRAME[1]);
      c.box(0.070, 0.048, 0.180, 0.006, -0.012, 0, BK.lower);
      c.seg(0, -0.03, 0, 0, 0.130, 0, 0.019, BK.frameDark, 0.019, 8);
      // Stanchions. Anodised gold: real, and the one bright vertical the bike has when
      // it is standing in tree shade.
      c.surf(S_METAL[0], S_METAL[1]);
      for (const sz of [1, -1]) {
        c.seg(0.014, -0.020, sz * 0.064, 0.014, -0.330, sz * 0.064, 0.0185, BK.stanchion, 0.0185, 10);
      }
      // Stem + bar.
      c.surf(S_FRAME[0], S_FRAME[1]);
      c.box(0.075, 0.040, 0.048, 0.030, 0.140, 0, BK.frameDark, -0.10);
      const bar: [number, number, number][] = [
        [0.048, 0.150, 0.000],
        [0.046, 0.163, 0.130],
        [0.030, 0.171, 0.272],
        [0.004, 0.173, BAR_WIDTH * 0.5],
      ];
      for (const sz of [1, -1]) {
        for (let i = 0; i < bar.length - 1; i++) {
          c.rod(bar[i][0], bar[i][1], bar[i][2] * sz,
            bar[i + 1][0], bar[i + 1][1], bar[i + 1][2] * sz, 0.0155, BK.bar, 0.0155, 8);
        }
        c.seg(0.036, 0.170, sz * 0.212, 0.098, 0.128, sz * 0.198, 0.008, BK.shaft, 0.006, 6);
        c.box(0.040, 0.036, 0.034, 0.040, 0.166, sz * 0.222, BK.frameDark);
      }
      // Grips.
      c.surf(S_RUBBER[0], S_RUBBER[1]);
      for (const sz of [1, -1]) {
        c.rod(0.020, 0.172, sz * 0.268, 0.006, 0.173, sz * (BAR_WIDTH * 0.5 - 0.012),
          0.019, BK.grip, 0.020, 8);
        c.axle(0.021, 0.172, sz * 0.262, 0.021, 0.012, BK.accent, 10);
      }
      c.shade(0.030, 0.116, 0, 0.120, 0.42);   // under the stem
      skin.part(c.build(), iSteer);

      // Hand targets: the middle of each grip, inside the steer bone so they follow
      // the bars through steering and everything else.
      for (const sz of [1, -1]) {
        const a = new THREE.Object3D();
        a.name = sz > 0 ? 'gripR' : 'gripL';
        a.position.set(0.013, 0.178, sz * (BAR_WIDTH * 0.5 - 0.055));
        this.steer.add(a);
        this.gripAnchor.push(a);
      }

      const l = new PartBuilder();
      l.underShade(0.34);
      l.surf(S_FRAME[0], S_FRAME[1]);
      // The lowers got a lot more scrutiny the moment they turned orange: in the old dark
      // grey the arch and the dropouts were boxes and nobody could see them, and against
      // the sky in Fox orange they read as three rectangular slabs bolted to two tubes.
      // A 38's lowers are a *casting* — one continuous swelling piece — so the legs taper
      // outward toward the axle, the arch is a rounded mass spanning them rather than a
      // slab, and each dropout is a blob with only a small flat for the axle boss.
      for (const sz of [1, -1]) {
        l.rod(0.014, -0.245, sz * 0.064, 0.020, -0.545, sz * 0.062, 0.024, BK.lower, 0.029, 9);
        l.blob(B.forkOffset - 0.004, -0.556, sz * 0.058, 0.030, 0.040, 0.019, BK.lower, 10, 0.12);
        l.box(0.030, 0.046, 0.024, B.forkOffset, -0.566, sz * 0.058, BK.lower, 0.10);
      }
      l.blob(0.026, -0.322, 0, 0.032, 0.050, 0.080, BK.lower, 12, 0.05);
      l.blob(0.020, -0.268, 0, 0.026, 0.030, 0.070, BK.lower, 10, 0.05);
      l.shade(0.030, -0.360, 0, 0.090, 0.34);   // under the arch
      l.box(0.050, 0.090, 0.040, -0.006, -0.470, -0.070, BK.frameDark);
      skin.part(l.build(), iForkLower);
    }

    // --- cranks, pedals, feet ------------------------------------------------------
    {
      const c = new PartBuilder();
      c.surf(S_FRAME[0], S_FRAME[1]);
      for (const sz of [1, -1]) {
        c.box(0.185, 0.036, 0.017, sz * 0.088, 0, sz * 0.050, BK.bar);
        c.seg(sz * CRANK_LEN, 0, sz * 0.050, sz * CRANK_LEN, 0, sz * PEDAL_Z, 0.009, BK.shaft, 0.009, 6);
      }
      c.axle(0, 0, 0, 0.019, 0.130, BK.shaft, 8);
      c.surf(S_METAL[0], S_METAL[1]);
      c.ring(0, 0, 0.052, 0.104, 0.005, 0.008, BK.ring, 4, 26);
      for (let i = 0; i < 26; i++) {
        const a = (i / 26) * Math.PI * 2;
        c.box(0.014, 0.010, 0.006, Math.cos(a) * 0.110, Math.sin(a) * 0.110, 0.052, BK.ring, a);
      }
      c.ring(0, 0, 0.052, 0.062, 0.006, 0.010, BK.ring, 4, 16);
      skin.part(c.build(), iCranks);

      // Pedal platforms counter-rotate so they stay level, exactly as a pedal on its
      // own bearing does — and the shoe rides on the platform, which is why a foot in
      // this build cannot slide no matter what the cranks do.
      for (let i = 0; i < 2; i++) {
        const p = new PartBuilder();
        p.surf(S_METAL[0], S_METAL[1]);
        p.box(0.098, 0.014, 0.104, 0, 0, 0, BK.pedal);
        for (const px of [-0.036, 0, 0.036]) {
          p.box(0.010, 0.026, 0.098, px, 0.004, 0, BK.frameDark);
        }
        // Shoe: ball of the foot over the spindle, so the ankle lands where the shin
        // wants it and the sole reads flat on the platform.
        p.underShade(0.40);
        p.surf(S_RUBBER[0], S_RUBBER[1]);
        p.box(0.265, 0.030, 0.104, -0.022, 0.024, 0, BK.sole);
        p.surf(S_LEATHER[0], S_LEATHER[1]);
        p.blob(-0.030, 0.062, 0, 0.126, 0.046, 0.051, BK.shoe, 10);
        p.blob(0.050, 0.048, 0, 0.070, 0.030, 0.047, BK.shoe, 10);
        p.blob(-0.106, 0.080, 0, 0.052, 0.050, 0.048, BK.shoe, 10);
        p.box(0.118, 0.014, 0.056, -0.020, 0.070, 0, BK.accent, -0.06);
        // The sole against the platform — the rider's one contact with the bike that
        // the chase camera sees in profile every frame.
        p.shade(-0.020, 0.006, 0, 0.130, 0.50);
        skin.part(p.build(), iPedal[i]);

        const a = new THREE.Object3D();
        a.name = i === 0 ? 'pedalR' : 'pedalL';
        this.pedal[i].add(a);
        this.pedalAnchor.push(a);
      }
    }

    // --- rider ---------------------------------------------------------------------
    // The rig binds against the cockpit as the bike is *drawn*, so resolve it once here
    // before the rider exists. Linear blend skinning distorts in proportion to how far a
    // bone has turned from the pose its weights were authored in, and the blend zones
    // this rider has — shoulder and hip — are exactly the ones that must not pinch.
    this.root.updateMatrixWorld(true);
    this.resolveCockpit();
    this.rig = new RiderRig(skin, this.cockpit);
    this.chassis.add(this.rig.root);

    // --- one mesh ------------------------------------------------------------------
    // Every bone is in the graph and at its bind transform, so this is the moment the
    // bind pose is defined. `Skin.build()` bakes these matrices into the vertices and
    // `Skin.skeleton()` inverts the same ones; nothing else may move a bone until both
    // have run or the two will disagree by exactly that motion.
    this.root.updateMatrixWorld(true);
    const geo = skin.build();
    const skeleton = skin.skeleton();
    const mesh = new THREE.SkinnedMesh(geo, createKitMaterial());
    // An identity bind matrix, not the mesh's own world matrix: three re-derives
    // `bindMatrixInverse` from `matrixWorld` every frame in attached bind mode, so the
    // mesh's placement cancels out and the vertices are driven by the bones alone.
    mesh.bind(skeleton, new THREE.Matrix4());
    mesh.castShadow = true;
    mesh.frustumCulled = false;
    mesh.name = 'bikeRider';
    this.root.add(mesh);

    // --- contact shadow ------------------------------------------------------------
    // The kit does cast into the shadow map, but a cast shadow from a low sun lands
    // metres away and leaves nothing under the tires. docs/BAR.md names objects floating
    // on the surface as the single most damaging artefact under this art direction, and
    // this is the fix: a cheap darkening blob per contact patch, laid on the ground
    // normal the sim already sampled, fading out as the wheel leaves.
    const tex = shadowTexture();
    for (let i = 0; i < 2; i++) {
      const g = new THREE.PlaneGeometry(i === 0 ? 0.80 : 0.88, i === 0 ? 0.52 : 0.58);
      g.rotateX(-Math.PI / 2);
      // Pure black on normal blending is exactly a multiply: dst*(1 - a). That matters
      // here because the scene is HDR and tone-mapped — blending toward a *near*-black
      // constant instead adds a floor that survives the grade, so the blob turns into a
      // flat grey decal on anything bright. Black also means `opacity` is a clean fade
      // as the wheel leaves the ground, which MultiplyBlending would not give.
      const mat = new THREE.MeshBasicMaterial({
        color: 0x000000, map: tex, transparent: true, opacity: SHADOW_ALPHA,
        depthWrite: false, toneMapped: false,
      });
      const m = new THREE.Mesh(g, mat);
      m.frustumCulled = false;
      m.renderOrder = -2;
      this.shadowMesh.push(m);
      this.shadowMat.push(mat);
      this.root.add(m);
    }
  }

  /**
   * Where the bars and the pedals actually ended up, in chassis space.
   *
   * Read out of the scene graph rather than recomputed, which is the whole reason a hand
   * cannot drift off a grip: it is solved against the bars as *drawn*, this frame, after
   * steering, fork compression and swingarm motion.
   */
  private resolveCockpit(): void {
    _inv.copy(this.chassis.matrixWorld).invert();
    this.cockpit.gripL.setFromMatrixPosition(this.gripAnchor[1].matrixWorld).applyMatrix4(_inv);
    this.cockpit.gripR.setFromMatrixPosition(this.gripAnchor[0].matrixWorld).applyMatrix4(_inv);
    this.cockpit.pedalL.setFromMatrixPosition(this.pedalAnchor[1].matrixWorld).applyMatrix4(_inv);
    this.cockpit.pedalR.setFromMatrixPosition(this.pedalAnchor[0].matrixWorld).applyMatrix4(_inv);
  }

  /** Pose the model from the sim state. Pure read — never writes back to the sim. */
  update(s: BikeState, alpha: number): void {
    void alpha;
    const B = Tuning.bike;
    const S = Tuning.suspension;
    const now = performance.now() * 0.001;
    const dt = this.lastT < 0 ? 1 / 60 : clamp(now - this.lastT, 0, 0.1);
    this.lastT = now;

    // See CRASH_SINK. Same rates as the rider's own blend, so the bike coming out of the
    // hill and the rider letting go of the bars are one event and not two.
    this.crashMix = damp(this.crashMix, s.crashed ? 1 : 0,
      s.crashed ? CRASH_LIFT.inRate : CRASH_LIFT.outRate, dt);
    const cm = this.crashMix < 1e-3
      ? 0 : this.crashMix * this.crashMix * (3 - 2 * this.crashMix);

    this.root.position.set(s.x, s.y - B.comHeight, s.z);
    this.root.rotation.set(0, -s.yaw, 0, 'YXZ');
    // Vehicle convention: yaw on the root, then pitch, then lean about the pitched
    // forward axis. 'ZXY' is what gives that composition order.
    this.chassis.rotation.set(s.lean, 0, s.pitch, 'ZXY');

    // Suspension, measured from sag (see the file header).
    const fSag = S.frontTravel * S.sagFraction;
    const rSag = S.rearTravel * S.sagFraction;
    this.forkLower.position.y = (s.front.travel - fSag) / this.cosH;
    const rise = s.rear.travel - rSag;
    this.rearArm.rotation.z = -Math.asin(clamp(rise / this.swingLen, -0.85, 0.85));

    // Wheels stop turning when the bike is down. `spin` is not zeroed by the sim until
    // the remount, so a crashed bike drawn straight off it has both wheels still spinning
    // at riding speed while it slides on its side, which reads as a video that was paused
    // on the wrong layer.
    const spinK = 1 - cm;
    this.frontWheel.rotation.z = wrap2Pi(this.frontWheel.rotation.z - s.front.spin * dt * spinK);
    this.rearWheel.rotation.z = wrap2Pi(this.rearWheel.rotation.z - s.rear.spin * dt * spinK);
    this.steer.rotation.y = s.steer;

    // Cranks. A bike freewheels: coasting, the pedals sit level and the outside foot
    // drops in a corner. Driving them off wheel speed spins them all the way down the
    // mountain, which every rider reads as wrong instantly.
    if (riderInput.pedal > 0.05 && cm === 0) {
      this.crank = wrap2Pi(this.crank - s.rear.spin * dt * GEAR);
    } else {
      // `lean` runs past 80 degrees in a crash, so the outside-foot term has to fade with
      // the fall or the cranks swing to a full quarter turn while the bike is on its side.
      const target = s.lean * OUTSIDE_FOOT * (1 - cm);
      const k = Math.round((this.crank - target) / Math.PI);
      this.crank = damp(this.crank, target + k * Math.PI, 5, dt);
    }
    this.cranks.rotation.z = this.crank;
    this.pedal[0].rotation.z = -this.crank;
    this.pedal[1].rotation.z = -this.crank;

    // Everything above moved the cockpit; resolve where it actually ended up before the
    // rider reaches for it.
    this.root.updateMatrixWorld(true);
    this.resolveCockpit();

    // --- the bike comes out of the hillside while it is down ---------------------------
    // Measured against the ground the SIM sampled under each wheel, not against a fresh
    // terrain lookup, so this cannot disagree with the surface physics is standing on.
    // The lift is applied to the drawn root only; nothing here writes to BikeState.
    //
    // Scoped to `crashed` deliberately — see CRASH_SINK. The 200 mm of ordinary-riding
    // burial is another piece's P0 this wave and papering over it here would hide it.
    if (cm > 0) {
      _v.setFromMatrixPosition(this.frontWheel.matrixWorld);
      let sink = s.front.ground.height - (_v.y - B.wheelRadius);
      _v.setFromMatrixPosition(this.rearWheel.matrixWorld);
      const rSink = s.rear.ground.height - (_v.y - B.wheelRadius);
      if (rSink > sink) sink = rSink;
      const lift = sink > 0 ? Math.min(sink, CRASH_LIFT.max) * cm : 0;
      if (lift > 0) {
        this.root.position.y += lift;
        this.root.updateMatrixWorld(true);
      }
      // Observed AFTER the lift, on purpose: an invariant that watched the raw sim state
      // would report this piece's own defect for ever and could never confirm the fix.
      // What has to be true is a property of the DELIVERED FRAME — the drawn wheel is not
      // inside the hill — so that is what is measured.
      //
      // ...and only once the fall is committed (`cm > 0.5`), which the first exercised run
      // of this invariant is what taught. Observed from the first frame of `crashed` it
      // reported 0.101 m against a 0.06 budget on 2 of 2416 samples, and both were in the
      // 0.05 s before the blend had ramped — i.e. it was reporting the sink the sim already
      // had at the instant of the crash, which is ride-feel's 200 mm ordinary-riding gap
      // arriving inside this window. The docstring above already says this number is
      // deliberately not that one; the gate is what makes that true rather than intended.
      // A drawn bike that never came out of the hill still fails it, because `cm` runs to 1
      // whatever the lift does.
      if (cm > 0.5) CRASH_SINK.observeAt(Math.max(0, sink - lift), s.x, s.y, s.z);
    }

    this.rig.update(s, this.cockpit, dt);

    // Contact shadows. root is a translation plus a yaw, so its inverse is a yaw and a
    // subtraction — no matrix needed.
    const cy = Math.cos(s.yaw);
    const sy = Math.sin(s.yaw);
    _v.setFromMatrixPosition(this.frontWheel.matrixWorld);
    this.placeShadow(0, s.front.cx, s.front.cy, s.front.cz,
      s.front.ground.nx, s.front.ground.ny, s.front.ground.nz,
      _v.y - (s.front.cy + B.wheelRadius), cy, sy);
    _v.setFromMatrixPosition(this.rearWheel.matrixWorld);
    this.placeShadow(1, s.rear.cx, s.rear.cy, s.rear.cz,
      s.rear.ground.nx, s.rear.ground.ny, s.rear.ground.nz,
      _v.y - (s.rear.cy + B.wheelRadius), cy, sy);
  }

  private placeShadow(
    i: number,
    wx: number, wy: number, wz: number,
    nx: number, ny: number, nz: number,
    gap: number, cy: number, sy: number,
  ): void {
    const mesh = this.shadowMesh[i];
    const mat = this.shadowMat[i];
    const t = clamp01(Math.max(0, gap) / 1.2);
    const a = SHADOW_ALPHA * (1 - t) * (1 - t);
    if (a < 0.012) { mesh.visible = false; return; }
    mesh.visible = true;
    mat.opacity = a;

    const dx = wx - this.root.position.x;
    const dy = wy - this.root.position.y;
    const dz = wz - this.root.position.z;
    const lx = dx * cy + dz * sy;
    const lz = -dx * sy + dz * cy;
    _v.set(nx * cy + nz * sy, ny, -nx * sy + nz * cy).normalize();
    mesh.quaternion.setFromUnitVectors(UPV, _v);
    mesh.position.set(lx + _v.x * 0.045, dy + _v.y * 0.045, lz + _v.z * 0.045);
    const sc = 1 + t * 0.9;
    mesh.scale.set(sc, 1, sc);
  }
}
